import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    category: 'makeup',
    subcategory: '',
    thumbnail: '',
    is_premium: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const response = await axios.get(`${API}/tutorials`);
      setTutorials(response.data);
    } catch (error) {
      console.error('Failed to fetch tutorials', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/tutorials`, formData);
      toast.success('Tutoriel ajouté avec succès!');
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        video_url: '',
        category: 'makeup',
        subcategory: '',
        thumbnail: '',
        is_premium: true
      });
      fetchTutorials();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du tutoriel');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tutoriel?')) return;

    try {
      await axios.delete(`${API}/admin/tutorials/${id}`);
      toast.success('Tutoriel supprimé');
      fetchTutorials();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12 bg-porcelain"
      data-testid="admin-page"
    >
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl text-charcoal tracking-tighter mb-2">
                Administration
              </h1>
              <p className="text-taupe">Gérez les tutoriels et le contenu</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                <span className="text-taupe">Chargement automatique: <span className="text-primary font-medium">Dimanches à minuit</span></span>
              </div>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              data-testid="admin-add-tutorial-btn"
              className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90 px-6 py-3 uppercase tracking-wide text-sm transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              Ajouter un tutoriel
            </button>
          </div>

          {/* Add Tutorial Form */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-silk p-8 border gold-border mb-12"
              data-testid="admin-form"
            >
              <h2 className="font-serif text-3xl text-charcoal tracking-tighter mb-6">
                Nouveau Tutoriel
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-taupe uppercase tracking-wide mb-2">
                      Titre
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      data-testid="admin-title-input"
                      className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-3 text-charcoal"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-taupe uppercase tracking-wide mb-2">
                      Catégorie
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      data-testid="admin-category-select"
                      className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-3 text-charcoal"
                      required
                    >
                      <option value="makeup">Maquillage</option>
                      <option value="hair">Coiffure</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-taupe uppercase tracking-wide mb-2">
                      Sous-catégorie (optionnel)
                    </label>
                    <input
                      type="text"
                      value={formData.subcategory}
                      onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                      data-testid="admin-subcategory-input"
                      className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-3 text-charcoal"
                      placeholder="Ex: Yeux, Lèvres, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-taupe uppercase tracking-wide mb-2">
                      URL de la vidéo
                    </label>
                    <input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      data-testid="admin-video-url-input"
                      className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-3 text-charcoal"
                      placeholder="https://youtube.com/..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-taupe uppercase tracking-wide mb-2">
                      URL de la miniature
                    </label>
                    <input
                      type="url"
                      value={formData.thumbnail}
                      onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                      data-testid="admin-thumbnail-input"
                      className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-3 text-charcoal"
                      placeholder="https://..."
                      required
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={formData.is_premium}
                      onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
                      data-testid="admin-premium-checkbox"
                      className="w-5 h-5 accent-primary"
                    />
                    <label className="text-sm text-charcoal uppercase tracking-wide">
                      Contenu Premium
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-taupe uppercase tracking-wide mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="admin-description-input"
                    className="w-full border border-primary/30 bg-transparent focus:border-primary focus:outline-none p-4 text-charcoal min-h-32"
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-submit-btn"
                    className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500 disabled:opacity-50"
                  >
                    {loading ? 'Ajout...' : 'Ajouter le tutoriel'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    data-testid="admin-cancel-btn"
                    className="bg-transparent border gold-border text-charcoal hover:bg-charcoal hover:text-white px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Tutorials List */}
          <div className="space-y-4" data-testid="admin-tutorials-list">
            <h2 className="font-serif text-3xl text-charcoal tracking-tighter mb-6">
              Tutoriels existants ({tutorials.length})
            </h2>

            {tutorials.length === 0 ? (
              <div className="text-center py-20 bg-silk border gold-border">
                <Video className="w-16 h-16 text-taupe mx-auto mb-4" />
                <p className="text-taupe">Aucun tutoriel pour le moment</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {tutorials.map((tutorial) => (
                  <motion.div
                    key={tutorial.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-silk p-6 border gold-border hover:border-primary transition-colors"
                    data-testid={`admin-tutorial-${tutorial.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex gap-6 flex-1">
                        <img
                          src={tutorial.thumbnail}
                          alt={tutorial.title}
                          className="w-32 h-20 object-cover border gold-border"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-serif text-xl text-charcoal">{tutorial.title}</h3>
                            {tutorial.is_premium && (
                              <span className="bg-primary px-2 py-1 font-accent text-xs text-white uppercase tracking-wider">
                                Premium
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-taupe mb-2">{tutorial.description}</p>
                          <div className="flex items-center gap-4 text-xs text-taupe uppercase tracking-wide">
                            <span>{tutorial.category}</span>
                            {tutorial.subcategory && <span>• {tutorial.subcategory}</span>}
                            <span>• {tutorial.likes} likes</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(tutorial.id)}
                        data-testid={`admin-delete-${tutorial.id}`}
                        className="p-3 border gold-border hover:bg-secondary hover:text-white hover:border-secondary transition-all duration-300 ml-4"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AdminPage;
